'use client';

import { useCallback } from 'react';

import { useAuthentication } from '@ravago/shared/authentication';
import { downloadPageDocument, getAllOrderedProducts, QuotationUtils } from '@ravago/shared/next-data-access/client';
import { RelatedDocument } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { DownloadOption } from '@ravago/shared/radiance/components/input-field/components/Download/Download';
import notification from '@ravago/shared/radiance/utils/notification/notification';
import { useTranslations } from '@ravago/shared/translations/client';

import { saveAs } from 'file-saver';

import { useDialogContext } from '../../../../hooks/use-dialog-context';
import { CorrelationService } from '../../../../services/tracking/correlation-token.service';
import { DocumentNameUtils } from '../../../../utils/document-name/document-name.utils';
import { TrackingUtils } from '../../../../utils/tracking/tracking.utils';
import { getLocalStorageWarningRemover } from '../utils/document-download.utils';

export interface UseRelatedDocumentOptions {
   globals: RendererGlobals;
   dynamicPage: { type?: string; id?: string; detailPageName?: string };
   relatedDocuments: { key: string; documents: RelatedDocument[] }[];
   canDownloadDocumentsAnonymously?: boolean;
   hasPublicTdsDownloadPermission: boolean;
   selectedUlTdsLanguage?: DownloadOption;
   selectedUlTdsUOM?: DownloadOption;
}

export interface UseRelatedDocumentsReturn {
   canDownloadDocument: (document: RelatedDocument) => boolean;
   handleDownload: (
      document: RelatedDocument,
      selectedLanguageId: string | undefined,
      selectedUOMId: string | undefined,
      isWarningPossible?: boolean,
      showIndividualLoadingScreen?: boolean
   ) => Promise<void>;
   handleViewDocument: (document: RelatedDocument, selectedLanguageId: string | undefined, selectedUOMId: string | undefined) => void;
   handleAllDownload: (documents: RelatedDocument[]) => Promise<void>;
}

export const useRelatedDocuments = ({
   dynamicPage,
   relatedDocuments,
   canDownloadDocumentsAnonymously,
   hasPublicTdsDownloadPermission,
   selectedUlTdsLanguage,
   selectedUlTdsUOM
}: UseRelatedDocumentOptions): UseRelatedDocumentsReturn => {
   const translate = useTranslations();
   const { dispatch } = useDialogContext();
   const { accessToken } = useAuthentication();

   const isAuthorized = !!accessToken || !!canDownloadDocumentsAnonymously;

   /**
    * Determines if a specific document can be downloaded by the current user.
    * Authenticated users can download all documents (based on their permissions).
    * Non-authenticated users can only download public TDS documents that are not internal when the feature is enabled.
    */
   const canDownloadDocument = useCallback(
      (document: RelatedDocument): boolean => {
         if (isAuthorized) return true;
         return hasPublicTdsDownloadPermission && document.type?.toUpperCase() === 'TDS' && !document.isInternal;
      },
      [isAuthorized, hasPublicTdsDownloadPermission]
   );

   /**
    * Fetches the document binary via the `/api/page-documents/...` API route (which already handles
    * authenticated, branded-TDS and public-TDS variants based on session + query params).
    * Returns a Blob ready to hand to `file-saver`.
    */
   const fetchDocumentBlob = useCallback(
      async (document: RelatedDocument, selectedLanguageId: string | undefined, selectedUOMId: string | undefined): Promise<Blob> => {
         const correlationId = CorrelationService.generateCorrelationId('dpcid');
         const fileName = DocumentNameUtils.determineAppropriateFileName(
            { ...document, language: selectedLanguageId ?? document.language },
            dynamicPage.detailPageName
         );

         return downloadPageDocument({
            pageType: dynamicPage.type ?? '',
            pageId: dynamicPage.id ?? '',
            documentId: document.id,
            fileName,
            language: document.isBrandedTds ? selectedLanguageId ?? document.language : undefined,
            unitSystem: document.isBrandedTds ? (selectedUOMId === 'imperial' ? 'IMPERIAL' : 'METRIC') : undefined,
            correlationId
         });
      },
      [dynamicPage]
   );

   const trackTdsDownload = useCallback(
      (document: { id: string; title: string }) => {
         Promise.all([getAllOrderedProducts(), QuotationUtils.getQuotations()]).then(([products, quotations]) => {
            if (!products?.content) return;
            const productIds: string[] = products.content
               .filter((prod) => !!prod.baseProductId)
               .map((product) => product.baseProductId?.toString() ?? '');
            if (quotations) {
               productIds.push(
                  ...quotations
                     .filter((quotation) => !!quotation.product.baseProductId)
                     .map((quotation) => quotation.product.baseProductId?.toString() ?? '')
               );
            }

            const trackingInfo = {
               product: { id: dynamicPage.id, name: dynamicPage.detailPageName },
               document,
               correlationId: CorrelationService.getCorrelationId('dpcid')
            };

            if (productIds.includes(dynamicPage.id ?? '')) {
               TrackingUtils.track('tds_download_previously_purchased', trackingInfo);
            } else {
               TrackingUtils.track('tds_download_new_lead', trackingInfo);
            }
         });
      },
      [dynamicPage]
   );

   const handleIsDocumentLoading = useCallback(
      (isDownloading: boolean, documentName?: string) => {
         if (dispatch) {
            dispatch({
               type: isDownloading ? 'open_dialog' : 'close_dialog',
               value: { dialogType: 'document-is-downloading', title: documentName }
            });
         }
      },
      [dispatch]
   );

   const handleAllDocumentsAreLoading = useCallback(
      (isDownloading: boolean) => {
         if (dispatch) {
            dispatch({
               type: isDownloading ? 'open_dialog' : 'close_dialog',
               value: { dialogType: 'multiple-documents-are-loading' }
            });
         }
      },
      [dispatch]
   );

   const handleDownload = useCallback(
      async (
         document: RelatedDocument,
         selectedLanguageId: string | undefined,
         selectedUOMId: string | undefined,
         isWarningPossible = true,
         showIndividualLoadingScreen = true
      ) => {
         if (!canDownloadDocument(document)) return;

         // Show internal document warning for authenticated users
         if (isAuthorized && document.isInternal && isWarningPossible && !getLocalStorageWarningRemover()) {
            if (dispatch) {
               dispatch({
                  type: 'open_dialog',
                  value: { dialogType: 'internal-document-warning', documentId: document.id }
               });
            }
            return;
         }

         if (showIndividualLoadingScreen) handleIsDocumentLoading(true, document.name);

         try {
            const blob = await fetchDocumentBlob(document, selectedLanguageId, selectedUOMId);

            if ((document.isBrandedTds || document.type.toUpperCase() === 'TDS') && accessToken) {
               trackTdsDownload({ id: document.id, title: document.description });
            }

            if (blob) {
               const fileName = DocumentNameUtils.determineAppropriateFileName(
                  { ...document, language: selectedLanguageId ?? document.language },
                  dynamicPage.detailPageName
               );
               saveAs(blob, fileName);
            }
         } catch (_) {
            notification(translate('shipment.documents.download-error'));
         } finally {
            if (showIndividualLoadingScreen) handleIsDocumentLoading(false);
         }
      },
      [canDownloadDocument, isAuthorized, dispatch, handleIsDocumentLoading, fetchDocumentBlob, trackTdsDownload, dynamicPage, translate, accessToken]
   );

   const handleViewDocument = useCallback(
      (document: RelatedDocument, selectedLanguageId: string | undefined, selectedUOMId: string | undefined) => {
         if (!canDownloadDocument(document)) return;

         const fileName = DocumentNameUtils.determineAppropriateFileName(
            { ...document, language: selectedLanguageId ?? document.language },
            dynamicPage.detailPageName
         );

         const params = new URLSearchParams({ filename: fileName });
         if (document.isBrandedTds) {
            params.set('language', selectedLanguageId ?? document.language);
            params.set('uom', selectedUOMId === 'imperial' ? 'IMPERIAL' : 'METRIC');
         }

         const url = `/api/page-documents/${dynamicPage.type}/${dynamicPage.id}/${document.id}?${params.toString()}`;
         window.open(url, '_blank', 'noopener,noreferrer');
      },
      [canDownloadDocument, dynamicPage]
   );

   const handleAllDownload = useCallback(
      async (documents: RelatedDocument[]) => {
         const allDocuments = relatedDocuments.map((byCat) => byCat.documents).flat();
         const results = documents.map((doc) => {
            const found = allDocuments.find((d) => d.id === doc.id);
            if (!found) return Promise.resolve();
            return handleDownload(
               found,
               found.isBrandedTds ? selectedUlTdsLanguage?.id : undefined,
               found.isBrandedTds ? selectedUlTdsUOM?.id : undefined,
               false,
               false
            );
         });

         handleAllDocumentsAreLoading(true);
         await Promise.all(results);
         handleAllDocumentsAreLoading(false);
      },
      [relatedDocuments, handleDownload, selectedUlTdsLanguage, selectedUlTdsUOM, handleAllDocumentsAreLoading]
   );

   return { canDownloadDocument, handleDownload, handleViewDocument, handleAllDownload };
};
